// How a school is named in a picker -- customer screens AND admin screens.
//
// Several schools share a name -- the Lincolns, the Roosevelts, the Washingtons
// -- so the name alone leaves a parent guessing which one is theirs. The city
// in parentheses is what the admin screens already show, and this is the one
// place that spelling is defined so the customer and admin pickers cannot drift
// apart the way two hand-rolled copies did.
//
// ONE implementation, and it took three tries to get there: the template was
// first inlined in two admin pickers, then extracted to `schoolOptionLabel` for
// them, and then written AGAIN here for the customer picker -- so the admin
// side never learned the "(Stockton) (Stockton)" lesson below. That second
// helper is gone; every picker imports this one. Never re-inline it, and never
// start a third.
//
// Falls back to the bare name: `cityName` is null for a school with no city
// record, and "Lincoln High ()" would be worse than "Lincoln High".
//
// Some names ALREADY carry a parenthetical, and the office has used it for two
// different things: the city ("Lincoln Elementary (Stockton)") and the district
// ("Lincoln Elementary (West Contra Costa)"). Appending blindly gave
// "Lincoln Elementary (Stockton) (Stockton)". So the test is whether the name
// already says the CITY -- a district in parentheses still leaves the city
// worth adding, which is why this does not simply skip any name ending in ")".
//
// City names are typed by hand and some carry a trailing space ("Sacramento "),
// which rendered as "Abraham Lincoln Elementary (Sacramento )". Trim for the
// label only -- the stored value is the office's to fix.
export const schoolLabel = (school) => {
  if (!school) return '';

  const city = String(school.cityName || '').trim();
  if (!city) return school.name;

  const name = String(school.name || '');
  if (name.toLowerCase().includes(city.toLowerCase())) return name;

  return `${name} (${city})`;
};

// Labels for a WHOLE list of schools, where the city is not always enough.
//
// Schools 5046 and 5077 are both "Lincoln Elementary" in Fresno -- one in
// Fresno Unified, one in Clovis Unified -- so the city leaves two identical
// rows in the picker and the chooser is guessing again. Where a label repeats
// within the list, the district is what separates them.
//
// The district goes on only where it is needed, and only where it actually
// helps: two rows with the same label AND the same district gain nothing from
// repeating it, so they are left alone rather than made longer. Every other
// label in the list stays short.
//
// Takes the list because a collision is a property of the list, not of a
// school -- there is no way to see it one row at a time.
//
// ONE pass over the list, then one lookup per row: the customer picker holds
// ~13,000 schools and re-labels them on every render, so anything that rescans
// the list per colliding row is a real cost, not a theoretical one.
const districtOf = (school) => String((school && school.districtName) || '').trim();

export const schoolLabels = (schools) => {
  const list = schools || [];
  const labels = list.map(schoolLabel);

  // label -> how many rows carry it, and how many of those carry each district.
  const seen = new Map();
  labels.forEach((label, i) => {
    const entry = seen.get(label) || { count: 0, districts: new Map() };
    entry.count += 1;
    const district = districtOf(list[i]);
    entry.districts.set(district, (entry.districts.get(district) || 0) + 1);
    seen.set(label, entry);
  });

  return labels.map((label, i) => {
    const entry = seen.get(label);
    if (entry.count < 2) return label;

    const district = districtOf(list[i]);
    if (!district) return label;
    // Two rows in the same district: the district can't tell them apart either.
    if (entry.districts.get(district) > 1) return label;

    return `${label} — ${district}`;
  });
};

export default schoolLabel;
